// apps/client/src/hooks/useDischargedLoans.ts
import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

interface UseDischargedLoansParams {
  companyId: string;
  /**
   * Calendar/financial year, OR the literal 'all' to span every year.
   * When 'all', records/totals/breakdown cover all years and `monthlyTrend`
   * returns per-year buckets (see `trendGranularity`).
   */
  year: number | 'all';
  yearType?: 'calendar' | 'financial';
  brokerId?: string;
  officeId?: string;
  templateId?: string;  // Filter by loan book/template
  entityCode?: string;
}

interface RefinanceMatchData {
  matchId: string;
  newLoanId: string;
  newLender: string | null;
  newSettlementDate: string;
  newLoanAmountCents: number;
  newBrokerDisplay: string | null;
  newOfficeId: string | null;
  status: 'pending' | 'confirmed';
  // Raw SSOT verification fields (refinance_matches) for the outcome-type editor.
  outcomeType: string | null;
  verificationStatus: string | null;  // raw rm.status (pending/confirmed/rejected)
  verifiedByEmail: string | null;
  verifiedAt: string | null;
  verificationNotes: string | null;
}

interface DischargedLoanRecord {
  loanId: string;
  brokerId: string;
  broker: string;
  customerName: string;
  lender: string | null;
  dischargeDate: Date;
  settlementDate: Date;
  loanAmountCents: number;
  originalCommissionCents: number;
  dischargeReason: string | null;
  dischargeSource: 'explicit' | 'inferred';  // explicit = discharged_commission record, inferred = clawback pattern
  // Refinance cross-reference fields
  refinanceStatus: 'truly_discharged' | 'refinanced' | 'refinanced_pending' | 'returning_client' | 'returning_client_pending';
  refinanceMatch?: RefinanceMatchData;
  // Loss-reason categorization (lost_loan_reasons SSOT, keyed by company_id+loan_id).
  // Meaningful only for truly-discharged loans; null when uncategorized.
  lossReason: string | null;
  lossReasonSetByEmail: string | null;
  lossReasonSetAt: string | null;  // ISO 8601
  lossNotes: string | null;
}

interface MonthlyTrend {
  period: string;          // canonical bucket key: 'YYYY-MM' (monthly) or 'YYYY' (per-year)
  month?: string;          // present only when trendGranularity === 'month' (back-compat)
  count: number;
  totalLoanValueCents: number;
}

interface BrokerBreakdown {
  brokerId: string;
  broker: string;
  count: number;
  totalLoanValueCents: number;
  refinancedCount: number;
  refinancedLoanValueCents: number;
  // Confirmed returning clients per broker (WS3).
  returningCount: number;
  returningLoanValueCents: number;
}

interface LossReasonBreakdownEntry {
  reason: string;       // COALESCE(loss_reason, 'unknown')
  count: number;
  valueCents: number;
}

interface UseDischargedLoansReturn {
  dischargedLoans: DischargedLoanRecord[];
  monthlyTrend: MonthlyTrend[];
  brokerBreakdown: BrokerBreakdown[];
  lossReasonBreakdown: LossReasonBreakdownEntry[];
  loading: boolean;
  error: string | null;
  totalDischargeCount: number;
  totalLoanValueCents: number;
  avgLoanSizeCents: number;
  totalLostTrailCents: number;
  // Unbounded aggregates (full date-windowed set, not the ≤2000 record slice).
  refinancedCount: number;        // has non-rejected refinance_match (incl. pending)
  trulyDischargedCount: number;   // truly_discharged. refinancedCount + trulyDischargedCount = totalDischargeCount
  categorisedCount: number;       // truly-discharged AND loss_reason set; denominator = trulyDischargedCount
  refinancedLoanValueCents: number;        // refinancedLoanValueCents + trulyDischargedLoanValueCents = totalLoanValueCents
  trulyDischargedLoanValueCents: number;
  // Client-retention 5-status split (WS3). The five status counts sum to
  // totalDischargeCount: refinanced (confirmed) + returningClient (confirmed) +
  // refinancedPending + returningClientPending + trulyDischarged.
  returningClientCount: number;
  refinancedPendingCount: number;
  returningClientPendingCount: number;
  trendGranularity: 'month' | 'year';
  availableTemplateIds: string[];  // Templates with discharge data
  // Manual refresh. Pass { silent: true } to reconcile data WITHOUT flipping the
  // global `loading` flag — so a post-save refresh doesn't replace the whole page
  // with the loading spinner (which reads as a jarring full-page reload).
  refetch: (opts?: { silent?: boolean }) => Promise<void>;
  // Optimistic local patches for instant feedback after a save (the authoritative
  // values are reconciled by a subsequent silent refetch).
  patchReason: (loanId: string, reason: string, notes: string | null) => void;
  patchOutcome: (
    loanId: string,
    outcomeType: string | null,
    notes: string | null,
    status?: 'pending' | 'confirmed',
    relationshipType?: 'refinance' | 'returning_client'
  ) => void;
  // Roll back an optimistic patch to a captured row snapshot (call on save error).
  restoreLoan: (loanId: string, record: DischargedLoanRecord | undefined) => void;
}

/**
 * Hook to fetch discharged loans report from BigQuery
 *
 * Queries raw_commissions table to analyze discharged loans.
 * Returns individual discharge records with monthly trends and broker breakdown.
 * Now includes refinance status cross-referenced with refinance_matches table.
 *
 * RBAC: Automatically filters to broker's data if brokerId is provided
 */
export function useDischargedLoans({
  companyId,
  year,
  yearType = 'calendar',
  brokerId,
  officeId,
  templateId,
  entityCode,
}: UseDischargedLoansParams): UseDischargedLoansReturn {
  const [dischargedLoans, setDischargedLoans] = useState<DischargedLoanRecord[]>([]);
  const [monthlyTrend, setMonthlyTrend] = useState<MonthlyTrend[]>([]);
  const [brokerBreakdown, setBrokerBreakdown] = useState<BrokerBreakdown[]>([]);
  const [lossReasonBreakdown, setLossReasonBreakdown] = useState<LossReasonBreakdownEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalDischargeCount, setTotalDischargeCount] = useState(0);
  const [totalLoanValueCents, setTotalLoanValueCents] = useState(0);
  const [avgLoanSizeCents, setAvgLoanSizeCents] = useState(0);
  const [totalLostTrailCents, setTotalLostTrailCents] = useState(0);
  const [refinancedCount, setRefinancedCount] = useState(0);
  const [trulyDischargedCount, setTrulyDischargedCount] = useState(0);
  const [categorisedCount, setCategorisedCount] = useState(0);
  const [refinancedLoanValueCents, setRefinancedLoanValueCents] = useState(0);
  const [trulyDischargedLoanValueCents, setTrulyDischargedLoanValueCents] = useState(0);
  const [returningClientCount, setReturningClientCount] = useState(0);
  const [refinancedPendingCount, setRefinancedPendingCount] = useState(0);
  const [returningClientPendingCount, setReturningClientPendingCount] = useState(0);
  const [trendGranularity, setTrendGranularity] = useState<'month' | 'year'>('month');
  const [availableTemplateIds, setAvailableTemplateIds] = useState<string[]>([]);

  const fetchDischargedLoans = useCallback(async (opts?: { silent?: boolean }) => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    // Silent refresh (post-save) skips the full-page loading spinner.
    if (!opts?.silent) setLoading(true);
    setError(null);

    try {
      console.log(`[useDischargedLoans] Calling BigQuery getDischargedLoansReport:`, {
        companyId,
        year,
        yearType,
        brokerId: brokerId || 'all',
        officeId: officeId || 'all',
        templateId: templateId || 'all',
      });

      // Call BigQuery function
      const getDischargedLoansReportFunction = httpsCallable(functions, 'getDischargedLoansReport');
      const result = await getDischargedLoansReportFunction({
        companyId,
        year,
        yearType,
        brokerId,
        officeId,
        templateId,
        entityCode,
      });

      const data = result.data as {
        totalDischargeCount: number;
        totalLoanValueCents: number;
        avgLoanSizeCents: number;
        totalLostTrailCents: number;
        refinancedCount: number;
        returningClientCount: number;
        refinancedPendingCount: number;
        returningClientPendingCount: number;
        trulyDischargedCount: number;
        categorisedCount: number;
        refinancedLoanValueCents: number;
        trulyDischargedLoanValueCents: number;
        trendGranularity: 'month' | 'year';
        availableTemplateIds: string[];
        records: Array<{
          loanId: string;
          brokerId: string;
          brokerDisplay: string;
          customerName: string;
          lender: string | null;
          dischargeDate: string;
          settlementDate: string;
          loanAmountCents: number;
          originalCommissionCents: number;
          dischargeReason: string | null;
          dischargeSource: 'explicit' | 'inferred';
          refinanceStatus: 'truly_discharged' | 'refinanced' | 'refinanced_pending' | 'returning_client' | 'returning_client_pending';
          refinanceMatch?: {
            matchId: string;
            newLoanId: string;
            newLender: string | null;
            newSettlementDate: string;
            newLoanAmountCents: number;
            newBrokerDisplay: string | null;
            newOfficeId: string | null;
            status: 'pending' | 'confirmed';
            outcomeType: string | null;
            verificationStatus: string | null;
            verifiedByEmail: string | null;
            verifiedAt: string | null;
            verificationNotes: string | null;
          };
          lossReason: string | null;
          lossReasonSetByEmail: string | null;
          lossReasonSetAt: string | null;
          lossNotes: string | null;
        }>;
        monthlyTrend: Array<{
          period: string;
          month?: string;
          count: number;
          totalLoanValueCents: number;
        }>;
        brokerBreakdown: Array<{
          brokerId: string;
          brokerDisplay: string;
          count: number;
          totalLoanValueCents: number;
          refinancedCount: number;
          refinancedLoanValueCents: number;
          returningCount: number;
          returningLoanValueCents: number;
        }>;
        lossReasonBreakdown: Array<{
          reason: string;
          count: number;
          valueCents: number;
        }>;
      };

      console.log(`[useDischargedLoans] BigQuery returned:`, {
        totalDischargeCount: data.totalDischargeCount,
        totalLoanValueCents: (data.totalLoanValueCents / 100).toFixed(2),
        avgLoanSizeCents: (data.avgLoanSizeCents / 100).toFixed(2),
        records: data.records.length,
        availableTemplates: data.availableTemplateIds?.length || 0,
      });

      // Store aggregate metrics
      setTotalDischargeCount(data.totalDischargeCount);
      setTotalLoanValueCents(data.totalLoanValueCents);
      setAvgLoanSizeCents(data.avgLoanSizeCents);
      setTotalLostTrailCents(data.totalLostTrailCents || 0);
      setRefinancedCount(data.refinancedCount || 0);
      setReturningClientCount(data.returningClientCount || 0);
      setRefinancedPendingCount(data.refinancedPendingCount || 0);
      setReturningClientPendingCount(data.returningClientPendingCount || 0);
      setTrulyDischargedCount(data.trulyDischargedCount || 0);
      setCategorisedCount(data.categorisedCount || 0);
      setRefinancedLoanValueCents(data.refinancedLoanValueCents || 0);
      setTrulyDischargedLoanValueCents(data.trulyDischargedLoanValueCents || 0);
      setTrendGranularity(data.trendGranularity || 'month');
      setAvailableTemplateIds(data.availableTemplateIds || []);
      setLossReasonBreakdown(data.lossReasonBreakdown || []);

      // Transform BigQuery records to frontend format
      const transformedRecords: DischargedLoanRecord[] = data.records.map((record) => ({
        loanId: record.loanId,
        brokerId: record.brokerId,
        broker: record.brokerDisplay,
        customerName: record.customerName || 'Unknown',
        lender: record.lender,
        dischargeDate: new Date(record.dischargeDate),
        settlementDate: new Date(record.settlementDate),
        loanAmountCents: record.loanAmountCents,
        originalCommissionCents: record.originalCommissionCents,
        dischargeReason: record.dischargeReason,
        dischargeSource: record.dischargeSource,
        refinanceStatus: record.refinanceStatus,
        refinanceMatch: record.refinanceMatch ? {
          matchId: record.refinanceMatch.matchId,
          newLoanId: record.refinanceMatch.newLoanId,
          newLender: record.refinanceMatch.newLender,
          newSettlementDate: record.refinanceMatch.newSettlementDate,
          newLoanAmountCents: record.refinanceMatch.newLoanAmountCents,
          newBrokerDisplay: record.refinanceMatch.newBrokerDisplay,
          newOfficeId: record.refinanceMatch.newOfficeId,
          status: record.refinanceMatch.status,
          outcomeType: record.refinanceMatch.outcomeType ?? null,
          verificationStatus: record.refinanceMatch.verificationStatus ?? null,
          verifiedByEmail: record.refinanceMatch.verifiedByEmail ?? null,
          verifiedAt: record.refinanceMatch.verifiedAt ?? null,
          verificationNotes: record.refinanceMatch.verificationNotes ?? null,
        } : undefined,
        lossReason: record.lossReason ?? null,
        lossReasonSetByEmail: record.lossReasonSetByEmail ?? null,
        lossReasonSetAt: record.lossReasonSetAt ?? null,
        lossNotes: record.lossNotes ?? null,
      }));

      setDischargedLoans(transformedRecords);
      setMonthlyTrend(data.monthlyTrend);

      // Transform broker breakdown
      const transformedBrokerBreakdown: BrokerBreakdown[] = data.brokerBreakdown.map((broker) => ({
        brokerId: broker.brokerId,
        broker: broker.brokerDisplay,
        count: broker.count,
        totalLoanValueCents: broker.totalLoanValueCents,
        refinancedCount: broker.refinancedCount || 0,
        refinancedLoanValueCents: broker.refinancedLoanValueCents || 0,
        returningCount: broker.returningCount || 0,
        returningLoanValueCents: broker.returningLoanValueCents || 0,
      }));

      setBrokerBreakdown(transformedBrokerBreakdown);
    } catch (err: any) {
      console.error('[useDischargedLoans] Error:', err);
      // A SILENT (post-save) refresh that errors must NOT blank the page NOR trip the
      // page-level fatal `error` branch — the optimistic row update already succeeded,
      // so a transient background-reconcile failure should be invisible (logged only).
      // Only a FOREGROUND load surfaces the error UI and resets the data.
      if (!opts?.silent) {
        setError(err instanceof Error ? err.message : 'Failed to fetch discharged loans data');
        setDischargedLoans([]);
        setMonthlyTrend([]);
        setBrokerBreakdown([]);
        setLossReasonBreakdown([]);
        setTotalDischargeCount(0);
        setTotalLoanValueCents(0);
        setAvgLoanSizeCents(0);
        setTotalLostTrailCents(0);
        setRefinancedCount(0);
        setTrulyDischargedCount(0);
        setCategorisedCount(0);
        setRefinancedLoanValueCents(0);
        setTrulyDischargedLoanValueCents(0);
        setTrendGranularity('month');
        setAvailableTemplateIds([]);
      }
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [companyId, year, yearType, brokerId, officeId, templateId, entityCode]);

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchDischargedLoans();
  }, [fetchDischargedLoans]);

  // Expose refetch for manual refresh. { silent: true } reconciles without the spinner.
  const refetch = useCallback(async (opts?: { silent?: boolean }) => {
    await fetchDischargedLoans(opts);
  }, [fetchDischargedLoans]);

  // Optimistic local patches — instant UI feedback after a save; the next silent
  // refetch reconciles authoritative values (set-by email, aggregate counts, etc.).
  const patchReason = useCallback(
    (loanId: string, reason: string, notes: string | null) => {
      setDischargedLoans((prev) =>
        prev.map((l) =>
          l.loanId === loanId
            ? { ...l, lossReason: reason, lossNotes: notes ?? null, lossReasonSetAt: new Date().toISOString() }
            : l
        )
      );
    },
    []
  );

  const patchOutcome = useCallback(
    (
      loanId: string,
      outcomeType: string | null,
      notes: string | null,
      status?: 'pending' | 'confirmed',
      // Relationship the confirm asserts. Drives which confirmed status the row
      // flips to: a returning-client confirm must show 'returning_client', not
      // 'refinanced'. Omitted (save-outcome on an already-typed row) keeps the
      // legacy 'refinanced' default so existing callers are unchanged.
      relationshipType?: 'refinance' | 'returning_client'
    ) => {
      setDischargedLoans((prev) =>
        prev.map((l) => {
          if (l.loanId !== loanId || !l.refinanceMatch) return l;
          const confirmedStatus =
            relationshipType === 'returning_client' ? 'returning_client' : 'refinanced';
          return {
            ...l,
            refinanceStatus: status === 'confirmed' ? confirmedStatus : l.refinanceStatus,
            refinanceMatch: {
              ...l.refinanceMatch,
              outcomeType: outcomeType ?? null,
              verificationNotes: notes ?? null,
              ...(status ? { status } : {}),
              verifiedAt: new Date().toISOString(),
            },
          };
        })
      );
    },
    []
  );

  // Roll back an optimistic patch: restore a loan row to a captured snapshot.
  // Used when the background verify/save call fails so the row does not stay
  // showing a state the server rejected (financial data - never leave a false
  // 'Confirmed' on screen).
  const restoreLoan = useCallback(
    (loanId: string, record: DischargedLoanRecord | undefined) => {
      if (!record) return;
      setDischargedLoans((prev) => prev.map((l) => (l.loanId === loanId ? record : l)));
    },
    []
  );

  return {
    dischargedLoans,
    monthlyTrend,
    brokerBreakdown,
    lossReasonBreakdown,
    loading,
    error,
    totalDischargeCount,
    totalLoanValueCents,
    avgLoanSizeCents,
    totalLostTrailCents,
    refinancedCount,
    returningClientCount,
    refinancedPendingCount,
    returningClientPendingCount,
    trulyDischargedCount,
    categorisedCount,
    refinancedLoanValueCents,
    trulyDischargedLoanValueCents,
    trendGranularity,
    availableTemplateIds,
    refetch,
    patchReason,
    patchOutcome,
    restoreLoan,
  };
}
